import { type ReactElement } from 'react'
import { DateTime } from 'luxon'
import { isDefined, isEmpty, isNotEmpty } from '@terros/common'
import { Link } from '@tanstack/react-router'
import { Stack, Text, Title } from '@mantine/core'
import { useHomeScreenAnnouncement, useTranslation } from '@front/model'
import { DateRangeMenu, NotReady, Page, TButton } from '@components'
import { DisplayAnnouncement } from '../Announcement'
import { useHomeDashboardFeed, type HomeDashboardFeedState } from './useHomeDashboardFeed'
import { HustleFeedSection } from './HustleFeedSection'
import { DashboardFeedSection } from './DashboardFeedSection'

/**
 * The Home feed: the hustle widget pinned first, then each dashboard as its
 * own labeled section — mirroring the mobile Home. Edit goes to the Dashboards
 * list, the one place every dashboard the viewer can manage is edited.
 */
export function HomeDashboardFeed(): ReactElement {
  const { t } = useTranslation()
  const feed = useHomeDashboardFeed()
  const announcement = useHomeScreenAnnouncement()

  return (
    <Page>
      <Page.Heading isFetching={feed.isFetching} refetch={feed.refetch} title={<HomeTitle />}>
        <DateRangeMenu
          onChange={feed.selectHomeDateRange}
          onReset={isDefined(feed.homeDateRange) ? feed.clearHomeDateRange : undefined}
          placeholder={t('Date range')}
          resetLabel={t('Clear date range')}
          value={feed.homeDateRange}
        />
        <TButton component={Link} icon='tune' to='/dashboard' variant='secondary'>
          {t('Edit')}
        </TButton>
      </Page.Heading>
      <Page.Content scrollArea>
        <Stack gap='xl'>
          <DisplayAnnouncement announcement={announcement} />
          <FeedContent feed={feed} />
        </Stack>
      </Page.Content>
    </Page>
  )
}

function HomeTitle(): ReactElement {
  const { t } = useTranslation()
  return (
    <Stack gap={0}>
      <Title order={2}>{t('Home')}</Title>
      <Text c='dimmed' size='sm'>
        {DateTime.now().toFormat('cccc, LLLL d')}
      </Text>
    </Stack>
  )
}

function FeedContent({ feed }: { feed: HomeDashboardFeedState }): ReactElement {
  const { t } = useTranslation()

  if (feed.isLoading) return <NotReady id='Dashboards' inline isLoading />
  if (feed.error) return <NotReady error={feed.error} id='Dashboards' inline />

  const hustleDashboard = feed.hustleDashboard
  const showsHustle = isDefined(hustleDashboard) && isNotEmpty(feed.hustleWidgets)
  if (!showsHustle && isEmpty(feed.groups)) {
    return (
      <Page.Hero
        description={t('Create one from the Dashboards page.')}
        icon='space_dashboard'
        title={t('No dashboards yet')}
      />
    )
  }

  return (
    <>
      {showsHustle && (
        <HustleFeedSection
          dashboard={hustleDashboard}
          dateRange={feed.sectionDateRange(hustleDashboard)}
          widgets={feed.hustleWidgets}
        />
      )}
      {feed.groups.map((group) => (
        <DashboardFeedSection
          dateRange={feed.sectionDateRange(group.dashboard)}
          group={group}
          homeDateRange={feed.homeDateRange}
          key={group.dashboard.dashboardId}
          onClearDateRange={() => feed.clearSectionDateRange(group.dashboard.dashboardId)}
          onSelectDateRange={(range) => feed.selectSectionDateRange(group.dashboard.dashboardId, range)}
          selectedDateRange={feed.sectionSelections.get(group.dashboard.dashboardId)}
        />
      ))}
    </>
  )
}
